/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import dayjs from "dayjs"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { optionValue } from "../consts"
import { MavConstant } from "./IncidentsDrawer/mavConstants"

type option = {
  label: string
  value: string
}

export const onSelectOption = (
  val: string,
  selectedItems: string[],
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>,
  options: option[],
) => {
  const otherOptions = options.filter(
    option => option.value !== optionValue.ALL,
  )

  // if 'all' is selected, select all other options OR if all other options are selected, select 'all' option
  if (
    val === optionValue.ALL ||
    (val !== optionValue.ALL &&
      selectedItems.length === otherOptions.length - 1)
  ) {
    setSelectedItems(options.map(option => option.value))
  } else {
    // default
    setSelectedItems([...selectedItems, val])
  }
}

export const onDeselectOption = (
  val: string,
  selectedItems: string[],
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>,
  options: option[],
) => {
  // if 'all' is deselected, deselect all other options
  if (val === optionValue.ALL) {
    setSelectedItems([])
  } else if (selectedItems.length === options.length) {
    // when selectedAll: if other deselect, deselect 'all' option
    const filteredIncidentType = selectedItems.filter(
      value => value !== optionValue.ALL && value !== val,
    )
    setSelectedItems(filteredIncidentType)
  } else {
    // default
    const filteredIncidentType = selectedItems.filter(option => option !== val)
    setSelectedItems(filteredIncidentType)
  }
}

// Will include appeal types after fix BE issue - BE cannot combined appease and appeal types properly
const resolutionTypeContentKey = {
  [MavConstant.appeaseTypeNotRefunded]: "incidents.resolutionType.noAction",
  [MavConstant.appeaseTypeRefunded]: "incidents.resolutionType.refundedByGrab",
  [MavConstant.appeaseTypeIssuedVoucher]:
    "incidents.resolutionType.voucherIssuedByGrab",
  [MavConstant.chargeBackFilter]: "incidents.resolutionType.chargebackByGrab",
  // [MavConstant.appealApproved]: "incidents.resolutionType.fulfilled",
  // [MavConstant.appealRejected]: "incidents.resolutionType.rejected",
  // [MavConstant.appealPartialApproved]:
  //   "incidents.resolutionType.partiallyFulfilled",
} as const

export const useResolutionTypeMap = () =>
  useTranslationsMapTyped(resolutionTypeContentKey)

const formatDateForEndpoint = "YYYY-MM-DDTHH:mm:ss.SSS[Z]"

export const formatDateForDisplay = "YYYY-MM-DD"

export const lowerCaseFirstLetter = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1)
}

// Convert browser time to UTC for API calls
export const convertBrowserTimeToUTC = (
  browserDate: string,
  isEndOfDay: boolean = false,
) => {
  const dayjsDate = dayjs(browserDate)
  const adjustedDate = isEndOfDay
    ? dayjsDate.endOf("day")
    : dayjsDate.startOf("day")
  return adjustedDate.utc().format(formatDateForEndpoint)
}

// Default browser time ranges (7 days ago from now in user's timezone)
export const getDefaultFromTimeBrowser = (daysAgo: number = 7) => {
  return dayjs()
    .subtract(daysAgo, "days")
    .startOf("day")
    .format(formatDateForDisplay) // "YYYY-MM-DD" in browser timezone
}

export const getDefaultEndTimeBrowser = () => {
  return dayjs()
    .endOf("day")
    .format(formatDateForDisplay) // "YYYY-MM-DD" in browser timezone
}

// Convert UTC to browser time for display
export const formatUTCForDisplay = (utcString: string) => {
  return dayjs
    .utc(utcString)
    .local()
    .format(formatDateForDisplay)
}
